import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { connect, useSelector } from 'react-redux';
import styles from './searchDetail.module.scss';

import { RESPOND_PROVIDERS_WARNING, FEATURE_FLAGS } from 'utils/constants';
import { SearchEntity, User, GenericObject, Permission } from 'v2/models';
import { REVIEW_STATUS } from 'v2/utils/searchUtils';
import { FeatureToggle } from 'react-feature-toggles';

import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import SubmitToNomadButton from 'v2/common/SubmitToNomadButton/SubmitToNomadButton';
import ReviewerAssignToMeButton from 'v2/common/ReviewerAssignToMeButton/ReviewerAssignToMeButton';

import ReviewerAssignment from 'v2/common/ReviewerAssignment/ReviewerAssignment';
import { isRequestViewerOnly } from 'v2/utils/user';
import SearchActivityTab from 'v2/common/DRM/ActivityTab/SearchActivityTab';
import { getKeyCount } from 'v2/utils/proposalUtils';
import ResponseReviewerConversation from 'v2/common/ResponseReviewerSelect/ResponseReviewerConversation';
import SearchDetailsLeftContent from './SearchDetailsLeftContent';
import ExtendedTooltip from 'v2/common/ExtendedRequest/ExtendedTooltip';

import LinkEntityButton from 'v2/common/LinkEntity/LinkEntityButton';
import { useIsDRMUser } from 'v2/hooks';
import LoadingSpinner from 'v2/common/LoadingSpinner/LoadingSpinner';
import { useLinkCreatedInfiniteActivities } from 'v2/queries';
import { useInvalidateSearchCounts } from 'v2/queries/searches/useInvalidateSearchCounts';
import { EditRequestLink } from './BottomButtons/EditRequestButton';
import { ArchiveRequestButton } from './BottomButtons/ArchiveRequestButton';
import { ResubmitRequestButton } from './BottomButtons/ResubmitRequestButton';
import { LinkedEntitiesList } from './LinkedEntities';
import { RenewRequestButton } from './BottomButtons/RenewRequestButton';
import { getIsSearchRenewable } from './BottomButtons/useRenewSearch';
import {
  useFreeUser,
  useArchiveFreeSearchMutation,
} from 'v2/hooks/useFreeUser';

enum TabKeys {
  AllActivity = 'all activity',
  Links = 'links',
}

type TabEvent = {
  eventKey: TabKeys;
  title: string;
};

const TABS: TabEvent[] = [
  {
    eventKey: TabKeys.AllActivity,
    title: 'all activity',
  },
];

const LINKS_TAB = {
  eventKey: TabKeys.Links,
  title: 'links',
};

interface StateProps {
  permissions: { [key: string]: Permission };
}

interface OwnProps {
  user: User;
  search: SearchEntity;
  className?: string;
  hasUnreadResponses: boolean;
  archiveSearch: (searchId: number) => Promise<unknown>;
  updateSearch: (searchDetails: Partial<SearchEntity>) => Promise<unknown>;
  onDetailOutdated?: () => void;
  counterToGoBack: number;
  comingFromPath?: string;
}

type SearchDetailProps = StateProps & OwnProps;

type GetTabsProps = {
  hasLinkedEntities: boolean;
};

function getTabs({ hasLinkedEntities }: GetTabsProps): TabEvent[] {
  const tabs = [...TABS];

  if (hasLinkedEntities) {
    tabs.push(LINKS_TAB);
  }

  return tabs;
}

function useContactedProviders(searchID: number): number {
  const selectApprovedProposals = useSelector((state: GenericObject) => {
    return getKeyCount(state.proposals.multipleProposals);
  });

  return selectApprovedProposals[searchID] ?? 0;
}

const SearchDetail = ({
  user,
  search,
  className,
  hasUnreadResponses,
  archiveSearch,
  updateSearch,
  counterToGoBack,
  onDetailOutdated,
  permissions,
  comingFromPath,
}: SearchDetailProps) => {
  const [isFreeUser] = useFreeUser();
  const history = useHistory();
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [isReSubmittingToNomadInProgress, setIsReSubmittingToNomadInProgress] =
    useState(false);

  const invalidateSearchCounts = useInvalidateSearchCounts();

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archivingInProgress, setArchivingInProgress] = useState(false);
  const [tabSelected, setTabSelected] = useState<TabKeys>(TABS[0].eventKey);
  const isDRMUser = useIsDRMUser();
  const { mutate: archiveFreeSearch } = useArchiveFreeSearchMutation(search.id);

  const {
    data: infiniteActivities,
    isLoading: isLinkCreatedActivitiesLoading,
  } = useLinkCreatedInfiniteActivities({ searchID: search.id });
  const linkCreatedActivities = infiniteActivities?.pages.flatMap(
    (page) => page.results
  );
  const linkedEntities = linkCreatedActivities?.flatMap(
    (activity) => activity.relationships
  );

  const tabs = getTabs({
    hasLinkedEntities: Boolean(linkedEntities?.length),
  });

  useEffect(() => {
    if (tabs.length === 1 && linkedEntities?.length === 0) {
      setTabSelected(tabs[0].eventKey);
    }
  }, [tabs, setTabSelected, linkedEntities]);

  const isSubmittedByCurrUser =
    search?.submitted_by?.username === user.username;

  const isSearchEditable = isSubmittedByCurrUser && search.editable;

  const isSearchInEditingAndSubmittable =
    isSubmittedByCurrUser &&
    search.review_status === REVIEW_STATUS.NEEDS_EDITING;

  const isSearchReviewable =
    !isSubmittedByCurrUser &&
    search.review_status === REVIEW_STATUS.UNDER_REVIEW;

  const isExtendedSearch = search.extended;

  const contactedProviders = useContactedProviders(search.id);
  const isLinkable =
    search?.assigned_to?.username === user.username &&
    search.review_status !== REVIEW_STATUS.UNDER_REVIEW;

  const isCurrentSearchBadDescription =
    search.last_check_description &&
    search.last_check_description.result === 'bad';

  if (isLinkCreatedActivitiesLoading) {
    return <LoadingSpinner />;
  }

  const isSearchRenewable = getIsSearchRenewable(search);
  const isSearchArchivable = isSubmittedByCurrUser;

  const isThereAButtonToShow = [
    isSearchReviewable,
    isSearchInEditingAndSubmittable,
    isSearchEditable,
    isSearchArchivable,
    isLinkable,
    isSearchRenewable,
  ].some((x) => x);

  const onArchiveClick = async () => {
    setArchivingInProgress(true);
    if (isFreeUser) {
      archiveFreeSearch(undefined, {
        onSuccess: async () => {
          invalidateSearchCounts();
          history.go(counterToGoBack);
        },
      });
      return;
    }

    if (!hasUnreadResponses) {
      await archiveSearch(search.id);
      invalidateSearchCounts();
      history.go(counterToGoBack);
    } else {
      toast.error(RESPOND_PROVIDERS_WARNING);
    }
  };

  const handleClickResubmit = async () => {
    setIsReSubmittingToNomadInProgress(true);
    const submittedSearchDetails: Partial<SearchEntity> = {
      id: search.id,
      review_status: REVIEW_STATUS.UNDER_REVIEW,
      editable: false,
    };

    try {
      await updateSearch(submittedSearchDetails);
      history.push('/requests/requester/in-review/1');
      toast.success('Request re-submitted!');
    } catch (error) {
      toast.error(`Error re-submitting request. Contact your reviewer`);
    }
  };

  const handleClickArchiveRequest = () => setShowArchiveModal(true);

  const renderReviewerButtons = () => {
    return (
      <ReviewerAssignment user={user} search={search} permissions={permissions}>
        {isSearchReviewable && (
          <>
            <EditRequestLink
              searchId={search.id}
              isSearchInEditingAndSubmittable={isSearchInEditingAndSubmittable}
            />
            <ResponseReviewerConversation
              search={search}
              disabled={isSubmittingReview || !isSearchReviewable}
              onChange={() => setIsSubmittingReview(true)}
              onError={() => setIsSubmittingReview(false)}
              onComplete={onDetailOutdated}
            />
          </>
        )}
        {
          <LinkEntityButton
            searchId={search.id}
            comingFromPath={comingFromPath}
          />
        }
        {isSearchReviewable && (
          <>
            <FeatureToggle featureName={FEATURE_FLAGS.REVIEWER_ASSIGNMENT}>
              <ReviewerAssignToMeButton
                search={search}
                user={user}
                isLoading={isSubmittingReview}
                onComplete={onDetailOutdated}
              />
            </FeatureToggle>
            <div className="flex-grow-1"></div>
            <SubmitToNomadButton
              search={search}
              disabled={isSubmittingReview || isCurrentSearchBadDescription}
              onSubmit={() => setIsSubmittingReview(true)}
              onError={() => setIsSubmittingReview(false)}
              onComplete={onDetailOutdated}
              className={'btn-primary'}
              tooltipMessage={
                isCurrentSearchBadDescription
                  ? 'The request description needs to be improved before you are able to submit to Nomad Data.'
                  : ''
              }
            />
          </>
        )}
      </ReviewerAssignment>
    );
  };

  const shouldShowButtons = () => {
    return (
      search.active && !isRequestViewerOnly(permissions) && isThereAButtonToShow
    );
  };

  const renderSearchButtons = () => {
    return (
      <div className={classnames(styles.searchButtons)}>
        <div
          className={classnames(
            'm-0 container-xl d-flex align-items-center buttons pt-4',
            styles.searchButtonsContainer
          )}
        >
          {!isSearchReviewable &&
            (isSearchInEditingAndSubmittable || isSearchEditable) && (
              <EditRequestLink
                searchId={search.id}
                isSearchInEditingAndSubmittable={
                  isSearchInEditingAndSubmittable
                }
              />
            )}
          {isSearchArchivable && (
            <ArchiveRequestButton
              isSearchInEditingAndSubmittable={isSearchInEditingAndSubmittable}
              archivingInProgress={archivingInProgress}
              onClick={handleClickArchiveRequest}
              isSubmittedByCurrUser={isSubmittedByCurrUser}
            />
          )}
          {isSearchRenewable && <RenewRequestButton {...search} />}
          {isSearchInEditingAndSubmittable && (
            <ResubmitRequestButton
              onClick={handleClickResubmit}
              isReSubmittingToNomadInProgress={isReSubmittingToNomadInProgress}
            />
          )}
          {renderReviewerButtons()}
          {isExtendedSearch && (
            <ExtendedTooltip
              extended={isExtendedSearch}
              expires_at={search.expires_at}
              showText={true}
              isFreeSearch={search.free}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={classnames(
          'container-xl',
          styles.searchDetailsContainer,
          styles.heigh_details,
          {
            [styles.reduceHeight]: !shouldShowButtons(),
          },
          className
        )}
      >
        <div className={classnames('pr-0 d-flex', styles.leftContent)}>
          <SearchDetailsLeftContent
            search={search}
            contactedProviders={contactedProviders}
          />
        </div>
        {isDRMUser && (
          <div className={classnames(styles.tabs_container)}>
            <Tabs
              className={classnames('border-bottom-0', styles.scrolling_tabs)}
              activeKey={tabSelected}
              onSelect={(key) => setTabSelected(key as TabKeys)}
              role="tab"
              transition={false}
            >
              {tabs.map((item) => (
                <Tab
                  key={item.eventKey}
                  className="border-primary"
                  eventKey={item.eventKey}
                  unmountOnExit={true}
                  title={`${item.title}`}
                >
                  <div>
                    {tabSelected === 'all activity' && (
                      <SearchActivityTab search={search} />
                    )}

                    {tabSelected === TabKeys.Links && (
                      <div className={styles.linksTab}>
                        <LinkEntityButton
                          comingFromPath={comingFromPath}
                          searchId={search.id}
                        />

                        <LinkedEntitiesList search={search} />
                      </div>
                    )}
                  </div>
                </Tab>
              ))}
            </Tabs>
          </div>
        )}
        {shouldShowButtons() && renderSearchButtons()}
      </div>
      <ConfirmationModal
        className={styles.viewModals}
        key={search.id}
        centered={true}
        animation={true}
        show={showArchiveModal}
        onCancel={() => setShowArchiveModal(false)}
        onOk={onArchiveClick}
        title="Are you sure?"
      >
        <>
          Archiving a request will close this request. You will no longer
          receive new responses and existing chats for this request will be
          closed.
          <br />
          <br />
          This cannot be undone.
        </>
      </ConfirmationModal>
    </>
  );
};

const mapStateToProps = (state: GenericObject) => {
  return {
    permissions: state.permissions.permissions,
  };
};

export default connect<StateProps, null, OwnProps>(mapStateToProps)(
  SearchDetail
);
